import { CloseOutlined } from '@mui/icons-material';
import { Alert, Box, CircularProgress, IconButton } from '@mui/material';
import { createColumnHelper, RowSelectionState } from '@tanstack/react-table';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import Breadcrumb from '@/components/shared/Breadcrumb';
import { DistributorRoleSwapModal } from '@/components/shared/DistributorRoleSwapModal';
import RoleSelect from '@/components/shared/RoleSelect';
import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import IndeterminateCheckbox from '@/components/shared/Table/IndeterminateCheckbox';
import TableSelectionActions from '@/components/shared/Table/TableSelectionActions';
import { SectionTitle } from '@/components/shared/Typography/Typography';
import { useSnackbar } from '@/hooks/useSnackbar';
import { normalizeRole } from '@/shared/constants/userGroupRole';

import EditAutocomplete from './EditAutocomplete';
import EditGroupExport from './EditGroupExport';
import type { CompatibleGroupUser, EditPageProps, SwapModalData } from './EditPage.types';
import {
  createHandleAddUsers,
  createHandleRemoveSelectedUsers,
  createHandleRemoveUser,
  createHandleRoleChange,
  createHandleSwapConfirm,
} from './EditPageActions';

const columnHelper = createColumnHelper<CompatibleGroupUser>();

interface LocationState {
  returnSearchParams?: string;
}

function EditPage({
  route,
  group,
  isManagingUsers,
  breadcrumbItems,
  sectionTitle,
  organizationId,
  error,
  table,
  autocompleteProps,
  pagination,
  exportConfig,
}: EditPageProps) {
  const { id } = useParams<{ id: string }>();
  const groupId = id ? parseInt(id, 10) : undefined;
  const snackbar = useSnackbar();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [swapModalData, setSwapModalData] = useState<SwapModalData | null>(null);
  const location = useLocation();

  const state = location.state as LocationState | null;

  const breadcrumbPath = state?.returnSearchParams ? `${route}?${state.returnSearchParams}` : route;

  const breadcrumbs = [
    { label: breadcrumbItems.prevPathLabel, path: breadcrumbPath },
    { label: breadcrumbItems.currentPathLabel, path: breadcrumbItems.currentPath },
  ];

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setRowSelection({});
    pagination.onChange((prev) => ({ ...prev, pageIndex: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowSelection({});
    pagination.onChange({ pageIndex: 0, pageSize: newPageSize });
  };

  const ensureValidPageAfterRemove = useCallback(
    (newTotalCount: number) => {
      const maxPageIndex = Math.max(0, Math.ceil(newTotalCount / pagination.state.pageSize) - 1);
      if (pagination.state.pageIndex > maxPageIndex) {
        pagination.onChange((prev) => ({ ...prev, pageIndex: maxPageIndex }));
      }
    },
    [pagination.state.pageIndex, pagination.state.pageSize, pagination.onChange],
  );

  const handleAddUsers = createHandleAddUsers({
    groupId,
    isManagingUsers,
    group,
    onAddUsers: autocompleteProps.onAddUsers,
    snackbar,
  });

  const handleRemoveUser = useMemo(
    () =>
      createHandleRemoveUser({
        groupId,
        isManagingUsers,
        totalCount: table.totalCount,
        onRemoveSingleUser: table.onRemoveSingleUser,
        ensureValidPageAfterRemove,
        setRowSelection,
        snackbar,
      }),
    [table, groupId, isManagingUsers, ensureValidPageAfterRemove, snackbar],
  );

  const handleRemoveSelectedUsers = createHandleRemoveSelectedUsers({
    groupId,
    isManagingUsers,
    tableData: table.data,
    totalCount: table.totalCount,
    rowSelection,
    onRemoveUsersInBatch: table.onRemoveUsersInBatch,
    ensureValidPageAfterRemove,
    setRowSelection,
    snackbar,
  });

  const handleRoleChange = useMemo(
    () =>
      createHandleRoleChange({
        groupId,
        isManagingUsers,
        organizationId,
        tableData: table.data,
        onUpdateRole: table.onUpdateRole,
        getDistributorGroupConflict: table.getDistributorGroupConflict,
        getBudgetGroupConflict: table.getBudgetGroupConflict,
        setSwapModalData,
        setSwapModalOpen,
        snackbar,
      }),
    [groupId, organizationId, isManagingUsers, table, snackbar],
  );

  const handleSwapConfirm = createHandleSwapConfirm({
    groupId,
    swapModalData,
    onSwapConfirm: table.onSwapConfirm,
    setSwapModalOpen,
    setSwapModalData,
    snackbar,
  });

  const closeSwapModal = () => {
    setSwapModalOpen(false);
    setSwapModalData(null);
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Box sx={{ display: 'flex', justifyContent: 'left' }}>
            <IndeterminateCheckbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </Box>
        ),
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'left' }}>
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </Box>
        ),
        size: 5,
      }),
      columnHelper.accessor('email', {
        header: 'User',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('fullName', {
        header: 'Name',
        cell: (info) => info.getValue() || '-',
      }),
      ...(table.onUpdateRole
        ? [
            columnHelper.accessor('role', {
              header: 'Role',
              cell: (info) => {
                const user = info.row.original;
                const absoluteIndex = pagination.state.pageIndex * pagination.state.pageSize + info.row.index;
                return (
                  <RoleSelect
                    value={normalizeRole(info.getValue())}
                    onChange={(newRole) => {
                      if (user.userId) handleRoleChange(user.userId, newRole);
                    }}
                    testId={`role-select-${absoluteIndex}`}
                    disabled={
                      isManagingUsers ||
                      !!table.isRoleChanging ||
                      table.isRefetching ||
                      table.removeUserMutation.isPending ||
                      table.removeUsersBatchMutation.isPending
                    }
                  />
                );
              },
            }),
          ]
        : []),
      columnHelper.display({
        id: 'actions',
        header: () => <Box sx={{ textAlign: 'right', width: '100%' }}></Box>,
        cell: (info) => {
          const user = info.row.original;
          const absoluteIndex = pagination.state.pageIndex * pagination.state.pageSize + info.row.index;

          const isRemovingUser =
            table.removeUserMutation.variables?.userId === user.userId && (table.isRefetching || table.removeUserMutation.isPending);

          const isRemovingUserInBatch =
            table.removeUsersBatchMutation.variables?.users.some((userFromBatch) => userFromBatch.userId === user.userId) &&
            (table.isRefetching || table.removeUsersBatchMutation.isPending);

          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <IconButton
                aria-label='remove'
                data-testid={`remove-user-button-${absoluteIndex}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  handleRemoveUser(user);
                }}
                disabled={
                  isManagingUsers ||
                  isRemovingUser ||
                  table.removeUserMutation.isPending ||
                  table.isRefetching ||
                  isRemovingUserInBatch ||
                  !!table.isRoleChanging
                }
              >
                {isRemovingUser || isRemovingUserInBatch ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <CloseOutlined sx={{ fontSize: '14px' }} />
                )}
              </IconButton>
            </Box>
          );
        },
        size: 5,
      }),
    ],
    [
      pagination.state.pageIndex,
      pagination.state.pageSize,
      handleRemoveUser,
      handleRoleChange,
      isManagingUsers,
      table.isRefetching,
      table.removeUserMutation.variables?.userId,
      table.removeUserMutation.isPending,
      table.removeUsersBatchMutation.isPending,
      table.removeUsersBatchMutation.variables?.users,
      table.onUpdateRole,
      table.isRoleChanging,
    ],
  );

  const tableWithData = useBaseTable<CompatibleGroupUser>({
    tableData: table.data,
    columns,
    isRowSelectionEnabled: true,
    isColumnVisibilityEnabled: false,
    isColumnOrderEnabled: false,
    hasPagination: true,
    rowSelection,
    onRowSelectionChange: setRowSelection,
    pagination: pagination.state,
    onPaginationChange: pagination.onChange,
    paginationCount: table.totalCount,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
    getRowId: (row) => row.userId || '',
  });

  const selectedCount = Object.keys(rowSelection).length;

  const isExportDisabled = table.isLoading || table.data === undefined || table.data?.length === 0;

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-users'
      >
        Error loading users: {error.message}
      </Alert>
    );
  }

  return (
    <>
      <Breadcrumb items={breadcrumbs} />
      <SectionTitle data-testid={sectionTitle.testId}>{sectionTitle.title}</SectionTitle>
      <EditAutocomplete
        usersData={autocompleteProps.usersData}
        membershipData={autocompleteProps.membershipData}
        filter={autocompleteProps.filter}
        onFilteredSearch={autocompleteProps.onFilteredSearch}
        disabled={autocompleteProps.disabled}
        onApply={handleAddUsers}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <TableSelectionActions
          selectedCount={selectedCount}
          totalCount={table.data.length}
          onRemove={handleRemoveSelectedUsers}
          selectionLabel='Selected Users'
          removeButtonText='Remove Users'
          testIdPrefix='selected-users'
          disabled={isManagingUsers || selectedCount === 0}
        />
        {exportConfig ? (
          <EditGroupExport
            organizationId={organizationId}
            groupId={groupId}
            isExportDisabled={isExportDisabled}
            url={exportConfig.url}
            fileName={exportConfig.fileName}
          />
        ) : null}
      </Box>
      <BaseTable<CompatibleGroupUser>
        {...tableWithData}
        testId={table.testId}
        ariaLabel={table.ariaLabel}
        noDataMessage={table.noDataMessage}
        loadingData={{
          isLoading: table.isLoading,
          loadingMessage: 'Loading Users...',
        }}
      />
      {table.onSwapConfirm && (
        <DistributorRoleSwapModal
          open={swapModalOpen}
          onClose={closeSwapModal}
          onConfirm={handleSwapConfirm}
          swapModalData={swapModalData}
          loading={isManagingUsers || !!table.isRoleChanging || !!table.isSwappingRole}
        />
      )}
    </>
  );
}

export default EditPage;
